import { HttpClient, HttpClientModule } from "@angular/common/http";
import { CUSTOM_ELEMENTS_SCHEMA, Injector, NgModule } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { UpgradeModule } from "@angular/upgrade/static";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { EffectsModule } from "@ngrx/effects";
import {
  TranslateLoader,
  TranslateModule,
  TranslateService,
} from "@ngx-translate/core";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import { CollapseModule } from "ngx-bootstrap/collapse";
import { BsDropdownModule } from "ngx-bootstrap/dropdown";
import { TooltipModule } from "ngx-bootstrap/tooltip";
import { NgxIntlTelInputModule } from "ngx-intl-tel-input";
import { MomentModule } from "ngx-moment";
import { AdaptiveRulesModule } from "./adaptive-rules/adaptive-rules.module";
import { AminCentralModule } from "./admin-central/amin-central.module";
import { AppRoutingModule, routingComponents } from "./app-routing.module";
import { AppComponent } from "./app.component";
import { AuthenticationModule } from "./auth/authentication.module";
import { LoginComponent } from "./auth/login.component";
import { ConnectionProfilesModule } from "./connection-profile/connection-profiles.module";
import { setAppInjector } from "./core/app-injector.component";
import { CoreModule } from "./core/core.module";
import { CountryPickerComponent } from "./core/country-picker.component";
import { ModalCustomDialogsModule } from "./core/modal/modal-custom-dialogs.module";
import { SideNavigationBarComponent } from "./core/side-navigation-bar.component";
import { TopNavigationBarActions } from "./core/state/top-navigation-bar.actions";
import { TopNavigationBarEffects } from "./core/state/top-navigation-bar.effects";
import { TopNavigationBarComponent } from "./core/top-navigation-bar.component";
import { TopNavigationTwoWayUnreadNotificationComponent } from "./core/top-navigation-two-way-unread-notification.component";
import { CustomTranslationLoader } from "./custom-translation-loader";
import { FeatureFlagsModule } from "./feature-flag/feature-flag.module";
import { buildFactoryForUpgradeProvider } from "./hybrid/hybrid-helper";
import { UpgradedProviderService } from "./hybrid/upgraded-provider.service";
import { MessageListModule } from "./message-list/message-list.module";
import { MessagesModule } from "./messages/messages.module";
import { AdminSystemModule } from "./modules/admin/admin-system/admin-system.module";
import { AdminModule } from "./modules/admin/admin.module";
import { DashboardModule } from "./modules/dashboard/dashboard.module";
import { TwoWaySettingsComponent } from "./modules/two-way/components/two-way-settings/two-way-settings.component";
import { PreviewEditTemplateContextComponent } from "./preview-edit-template/preview-edit-template-context.component";
import { PreviewEditTemplatesModule } from "./preview-edit-template/preview-edit-templates.module";
import { AppointmentListModule } from "./reminders/appointment-list/appointment-list.module";
import { AppointmentsModule } from "./reminders/appointments/appointments.module";
import { PatientsModule } from "./reminders/patients/patients.module";
import { MissedRecallReportModule } from "./reports/missed-recall/missed-recall-report.module";
import { ServiceModule } from "./services/service.module";
import { SoundProfileModule } from "./sound-profile/sound-profile.module";
import { EntityModule } from "./state/entity/entity.module";
import { StateModule } from "./state/state.module";
import { SupportModule } from "./support/support.module";
import { UploadImagesModule } from "./upload-images/upload-images.module";
import { AnalyticsModule } from "./analytics/analytics.module";
import { TemplateSettingsComponent } from "./template-settings/template-settings.component";
import {AdminUserFeaturesModule} from "./admin-user-features/admin-user-features.module";
import { CommunicationsModule } from './communications/communications.module';
import { RouteReuseStrategy } from "@angular/router";
import { AppRouteReuseStrategy } from "./app-route-resuse-strategy";
import { TrackableItemsModule } from "./trackable-items/trackable-items.module";

/**
 * This defines providers with mappings between angularJS name and Angular name.
 * The providers are then used by UpgradedProviderService
 * AOT does not allow getting this list from
 */
const upgradedProviders = [
  {
    provide: "userProfile",
    useFactory: buildFactoryForUpgradeProvider("userProfile"),
    deps: ["$injector"],
  },
  {
    provide: "userSettings",
    useFactory: buildFactoryForUpgradeProvider("userSettings"),
    deps: ["$injector"],
  },
  {
    provide: "accountService",
    useFactory: buildFactoryForUpgradeProvider("accountService"),
    deps: ["$injector"],
  },
];

// Needed for AoT compilation
// tslint:disable-next-line:typedef only-arrow-functions
export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http);
}

@NgModule({
  declarations: [
    AppComponent,
    TopNavigationBarComponent,
    TopNavigationTwoWayUnreadNotificationComponent,
    routingComponents,
    SideNavigationBarComponent,
    CountryPickerComponent, // downgraded
    TwoWaySettingsComponent,
    PreviewEditTemplateContextComponent,
    TemplateSettingsComponent,
  ],
  imports: [
    CoreModule,
    BrowserModule,
    BrowserAnimationsModule, // Needed by TwoWayModule - but best to import here when using lazy loading
    AppRoutingModule,
    // InfiniteScrollModule, // Needed by TwoWayModule - but best to import here when using lazy loading
    // LazyLoadImageModule, // If needed - then place here if using lazy loading (same for CarouselModule, and HttpModule)
    FormsModule,
    ReactiveFormsModule,
    UpgradeModule,
    HttpClientModule,
    FontAwesomeModule,
    ModalCustomDialogsModule,
    DashboardModule,
    ServiceModule, // TODO: I think we can remove this and delete the module (since registering services in root)
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useClass: CustomTranslationLoader,
        useFactory: createTranslateLoader, // exported factory function needed for AoT compilation
        deps: [HttpClient],
      },
    }),

    StateModule,
    AdminModule,
    AdminSystemModule, // Currently needed here while system settings state is inside this module
    // For AppComponent
    BsDropdownModule.forRoot(), // import without forRoot() in other modules if needed
    TooltipModule.forRoot(),
    MomentModule.forRoot(), // Needed for amDateFormat pipe in TopNavigationBar

    CollapseModule.forRoot(),

    NgxIntlTelInputModule,
    MissedRecallReportModule, // TODO: Move to report module (or sub module lazy loaded on route)
    EntityModule,
    AuthenticationModule,
    PatientsModule,
    AppointmentsModule,
    AppointmentListModule,
    MessageListModule,
    AdaptiveRulesModule,
    EffectsModule.forFeature([TopNavigationBarEffects]),
    PreviewEditTemplatesModule,
    MessagesModule,
    SoundProfileModule,
    ConnectionProfilesModule,
    AminCentralModule,
    SupportModule,
    UploadImagesModule,
    AdminUserFeaturesModule,
    FeatureFlagsModule.forRoot(),
    AnalyticsModule.forRoot("G-ZDBQY4DDJ6"),
    CommunicationsModule,
    TrackableItemsModule
  ],
  providers: [
    {provide: RouteReuseStrategy, useClass: AppRouteReuseStrategy},
    UpgradeModule,
    upgradedProviders,
    // The following is necessary to let angularJS component wrappers get an injector
    // (otherwise fails when contents is "compiled" before being attached to the DOM, so they can't access the
    // $scope injectable defined on the parent downgraded component)
    // https://github.com/angular/angular/issues/17581
    { provide: "$scope", useExisting: "$rootScope" },
    TopNavigationBarActions,
  ],
  entryComponents: [
    LoginComponent,
    CountryPickerComponent,
    TwoWaySettingsComponent,
    PreviewEditTemplateContextComponent,
  ], // List components to downgrade
  bootstrap: [AppComponent],
  exports: [
    TopNavigationTwoWayUnreadNotificationComponent,
    PreviewEditTemplateContextComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA], // Allow Non-Angular elements named with dash case (-) like angularJS reload-btn
})
export class AppModule {
  // The following is used to avoid circular dependencies (however, I never got it working to fix the problem in dialog-wrapper.component
  constructor(
    private injector: Injector,
    private upgradedProviderService: UpgradedProviderService,
    public upgrade: UpgradeModule,
    translateService: TranslateService
  ) {
    setAppInjector(injector);

    translateService.addLangs(["en"]);
    translateService.setDefaultLang("en");
    translateService.use("en");
  }

  /**
   * This is a placeholder to stop the bootstrapper from complaining
   */
  // ngDoBootstrap(): void {
  // }

  /**
   * This is called by main after the angularJS module has been bootstrapped.
   * Now the angularJS injector should be available.
   */
  afterJSBootstrap(): void {
    this.upgradedProviderService.injectServices();
  }
}
